import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardForm,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { passportApi } from "../lib/passport-api";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await passportApi.auth.forgotPassword(email);
      // Neutral confirmation regardless of whether the account exists (no enumeration).
      setSubmitted(true);
    } catch {
      // The API never distinguishes known/unknown by status, so a rejection is a genuine infra error.
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold">Reset your password</CardTitle>
            <CardDescription>
              {submitted
                ? "Check your inbox for a reset link."
                : "Enter your email and we'll send you a link to reset your password."}
            </CardDescription>
          </CardHeader>
          {submitted ? (
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                If an account exists for{" "}
                <span className="font-medium">{email}</span>, you'll receive an
                email with a link to reset your password. The link expires in 1
                hour.
              </p>
              <Link
                to="/login"
                className="text-sm text-foreground underline-offset-4 hover:underline"
              >
                Back to sign in
              </Link>
            </CardContent>
          ) : (
            <CardForm onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium leading-none"
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Send reset link"}
                </Button>
                <p className="text-sm text-muted-foreground text-center">
                  Remembered your password?{" "}
                  <Link
                    to="/login"
                    className="text-foreground underline-offset-4 hover:underline"
                  >
                    Sign in
                  </Link>
                </p>
              </CardFooter>
            </CardForm>
          )}
        </Card>
      </div>
    </main>
  );
}
